import type { Dict } from './index';

/** Admin panel strings in English. Must have exactly the same keys as es.ts (enforced by the type). */
export const en: Dict = {
  meta: { title: 'Voxel Licensing · Administration' },
  common: {
    language: 'Language',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    add: 'Add',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    loading: 'Loading',
    previous: '← Previous',
    next: 'Next →',
    noResults: 'No results',
    range: '{from}–{to} of {total}',
    never: 'Never',
    none: 'None',
    noneF: 'None',
    name: 'Name',
    email: 'Email',
    notes: 'Notes',
    product: 'Product',
    customer: 'Customer',
    status: 'Status',
    type: 'Type',
    date: 'Date',
    created: 'Created',
    country: 'Country',
    description: 'Description',
    days: '{n} days',
    allProducts: 'All products',
    networkError: 'Cannot connect to the API',
    httpError: 'HTTP error {status}',
  },
  nav: {
    appName: 'Licensing',
    appSub: 'Administration · Voxel',
    dashboard: 'Dashboard',
    licenses: 'Licenses',
    customers: 'Customers',
    machines: 'Machines',
    products: 'Products',
    operations: 'Operations',
    offline: 'Offline activation',
    audit: 'Audit log',
    webhooks: 'Webhooks',
    reference: 'Reference',
    manual: 'User manual',
    api: 'API (OpenAPI)',
    admin: 'Administrator',
    corporateSession: 'Corporate sign-in',
    localSession: 'Local user',
    logout: 'Sign out',
  },
  license: {
    type: { perpetual: 'Perpetual', subscription: 'Subscription', trial: 'Trial' },
    mode: { node_locked: 'Per seat', floating: 'Floating' },
    status: { active: 'Active', suspended: 'Suspended', revoked: 'Revoked', expired: 'Expired' },
    activeDays: 'Active · {days} d',
  },
  lastSeen: {
    never: 'Never',
    offline: 'By file',
    offlineHelp: 'Activated by file on {date}: it does not connect to the server',
    offlineOnlyHelp: 'All its computers were activated by file: they do not connect to the server',
  },
  login: {
    subtitle: 'Licensing · Administration panel',
    corporate: 'Sign in with your corporate account',
    orLocal: 'or with a local user',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    noMethod: 'No sign-in method is configured in the API, or the API cannot be reached.',
    customerQuestion: 'Are you a customer who needs to activate an offline computer?',
    portalLink: 'Offline activation portal',
    completing: 'Completing sign-in…',
    back: 'Back to sign-in',
    badCredentials: 'Incorrect email or password',
    oidcNotConfigured: 'OIDC is not configured',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Overall status of the licenses of every product',
    kpiActive: 'Active licenses',
    kpiMachines: 'Activated machines',
    kpiFloating: 'Floating sessions in use',
    kpiExpiring: 'Expiring within 30 days',
    recent: 'Recent activations',
    seeAll: 'See all',
    machine: 'Computer',
    license: 'License',
    when: 'When',
    noActivations: 'No activations yet',
    expiring: 'Expiring soon',
    expires: 'Expires',
    noExpiring: 'No license expires in the next 30 days',
    inactive: 'No connection in 30 days ({n})',
    inactiveHelp: 'Active licenses that have not connected for more than 30 days: the customer may not be using them.',
    noInactive: 'Every active license has connected in the last 30 days',
    lastSeen: 'Last connection',
    byProduct: 'By product',
    code: 'Code',
    active: 'Active',
    total: 'Total',
  },
  licenses: {
    title: 'Licenses',
    subtitle: 'Search, filter and manage the licenses of every product',
    exportCsv: 'Export CSV',
    new: '+ New license',
    searchPlaceholder: 'Search by key, customer, email or notes…',
    anyStatus: 'Any status',
    anyType: 'Any type',
    anyMode: 'Any licensing mode',
    anyExpiry: 'Any expiry',
    expiringIn: 'Expiring within {n} days',
    anyEdition: 'Any edition',
    colLastSeen: 'Last connection',
    anyLastSeen: 'Any connection',
    inactiveFor: 'No connection for more than {n} days',
    neverActivated: 'Never activated',
    colKey: 'Key',
    colUsage: 'Usage',
    colExpires: 'Expires',
    empty: 'No licenses match the filters',
    sessionsShort: '{n} sess.',
  },
  createLicense: {
    title: 'New license',
    noCustomer: '— No customer —',
    mode: 'Licensing mode',
    modeNodeLocked: 'Per seat (machines)',
    modeFloating: 'Floating (concurrent)',
    modeHelpFloating: 'Concurrent sessions with checkout/heartbeat',
    modeHelpNodeLocked: 'Machines activated at the same time',
    seats: 'Seats',
    sessions: 'Concurrent sessions',
    updatesUntil: 'Updates until',
    updatesHelp: 'Empty = no updates period',
    duration: 'Duration (days)',
    maxVersion: 'Maximum version',
    maxVersionHelp: 'E.g. "2" allows any 2.x',
    noLimit: 'No limit',
    features: 'Features',
    noFeatures: 'The product has no features defined',
    edition: 'Edition',
    noEdition: '— No edition (custom) —',
    editionHelp: 'Fills in the edition\'s features and limits; you can adjust the features',
    editionLimits: 'Includes the edition\'s limits: {limits}',
    notesPlaceholder: 'Order number, contact…',
    notify: 'Email the key to the customer',
    submit: 'Create license',
  },
  licenseDetail: {
    back: '← Licenses',
    suspend: 'Suspend',
    suspendConfirm: 'Suspend the license? Apps will stop validating the next time they connect.',
    reasonPrompt: 'Reason (optional)',
    reactivate: 'Reactivate',
    extend: 'Extend',
    changeSeats: 'Change seats',
    revoke: 'Revoke',
    revokeConfirm: 'Revocation is permanent. Revoke the license?',
    data: 'Details',
    seats: 'Seats',
    sessions: 'Concurrent sessions',
    inUse: '{used} of {seats} in use',
    expires: 'Expires',
    updatesUntil: 'Updates until',
    noUpdatesPeriod: 'No updates period',
    offlineGrace: 'Offline grace',
    graceCustom: '{n} days (custom)',
    graceProduct: '{n} days (product default)',
    reason: 'Reason',
    createdAt: 'Created',
    entitlements: 'Entitlements',
    subscription: 'Subscription (Stripe)',
    openInStripe: 'Open in Stripe',
    subscriptionPlan: 'Plan',
    subscriptionState: 'Status',
    subscriptionStatus: {
      active: 'Active',
      trialing: 'Trialing',
      past_due: 'Past due',
      incomplete: 'Incomplete',
      unpaid: 'Unpaid',
      canceled: 'Canceled',
      incomplete_expired: 'Payment expired',
      paused: 'Paused',
    },
    subscriptionCanceling: 'Will not renew',
    subscriptionRenews: 'Next renewal',
    subscriptionEnds: 'Ends on',
    stripeCustomer: 'Stripe customer',
    invoices: 'Invoices (Holded)',
    invoiceNumber: 'Number',
    invoiceKind: 'Type',
    invoiceAmount: 'Amount',
    invoiceKinds: { invoice: 'Invoice', creditnote: 'Credit note' },
    invoiceStatus: {
      pending: 'Queued',
      issued: 'Issued',
      draft: 'Draft',
      failed: 'Failed',
      skipped: 'No invoice',
    },
    invoiceRetry: 'Retry',
    invoicesHelp:
      'Each Stripe payment creates an invoice in Holded (online sales series) and each refund a credit note. Failures are retried automatically; failed ones stay here to review and retry. "Draft" is the test mode (HOLDED_APPROVE=false).',
    subscriptionHelp:
      'Bought from the application. The license follows the subscription on its own: the edition changes with the plan, the expiry moves forward with each renewal (plus the margin) and it expires if the subscription is canceled or not paid. Plan and card changes and cancellations are made in Stripe.',
    features: 'Features',
    limits: 'Limits',
    maxVersion: 'Maximum version',
    noLimit: 'No limit',
    machines: 'Activated machines ({n})',
    deactivatedList: 'Deactivated ({n})',
    floatingSessions: 'Floating sessions',
    colMachine: 'Machine',
    colSince: 'Since',
    colLastHeartbeat: 'Last heartbeat',
    colExpires: 'Expires',
    noSessions: 'No sessions',
    leaseInUse: 'In use',
    leaseReturned: 'Returned',
    leaseExpired: 'Expired',
    release: 'Release',
    history: 'History',
    colDevice: 'Computer',
    colSystem: 'System',
    colVersion: 'Version',
    colActivated: 'Activated',
    colLastSeen: 'Last seen',
    noMachines: 'No activated machines',
    offlineTag: 'offline',
    deactivatedAgo: 'Deactivated {when} ({reason})',
    reasonClient: 'by the customer',
    reasonAdmin: 'by an admin',
    deactivate: 'Deactivate',
    deactivateConfirm: 'Force deactivation of {name}? This frees the seat.',
    thisMachine: 'this machine',
    extendTitle: 'Extend license',
    extendUpdatesTitle: 'Extend updates period',
    daysToAdd: 'Days to add',
    daysToAddHelp: 'Added to the current expiry date ({date}), or to today if it has already expired',
    seatsTitle: 'Change number of seats',
    seatsHelp: 'Reducing seats does not kick out machines already activated; it only blocks new activations',
    entitlementsTitle: 'Edit entitlements',
    limitsJson: 'Numeric limits (JSON)',
    limitsInvalid: 'Limits must be a JSON object, e.g. {"max_projects": 10}',
    maxVersionHelp: '"2" allows 2.x; empty = no limit',
    changesNote: 'Changes reach the apps on their next online validation.',
    edition: 'Edition',
    lastSeen: 'Last connection',
    noEdition: 'No edition (custom)',
    editionHelp: 'Choosing an edition loads its features and limits; you can adjust them before saving',
    changeCustomer: 'Change',
    customerTitle: 'Change customer',
    customerHelp: 'The key and the activated machines stay the same: the app keeps working as before.',
    customerStripeNote: 'Subscription invoices are still issued with the customer details held in Stripe.',
  },
  products: {
    title: 'Products',
    subtitle: 'Voxel desktop applications with their modules and versions',
    new: '+ New product',
    colCode: 'Code',
    colGrace: 'Offline grace',
    colRefresh: 'Revalidation',
    colTrial: 'Trial',
    everyHours: 'every {n} h',
    no: 'No',
    empty: 'No products',
    editTitle: 'Edit product',
    newTitle: 'New product',
    code: 'Code',
    codeHelp: 'Identifier used by the apps (lowercase, hyphens)',
    graceDays: 'Offline grace (days)',
    graceHelp: 'How long the app can run without connecting',
    refreshHours: 'Revalidate every (hours)',
    leaseMinutes: 'Floating lease (minutes)',
    leaseHelp: 'Without a heartbeat, the session is released after this time',
    offlineDays: 'Offline activation validity (days)',
    allowTrial: 'Allow trial period',
    trialDays: 'Trial days',
    back: '← Products',
    licenseCount: '{n} licenses',
    viewLicenses: 'View licenses',
    deleteConfirm: 'Delete the product?',
    config: 'Licensing settings',
    grace: 'Offline grace',
    refresh: 'Revalidation',
    refreshEvery: 'every {n} hours',
    lease: 'Floating lease',
    leaseValue: '{n} minutes',
    offline: 'Offline activation',
    offlineValue: 'valid for {n} days',
    trial: 'Trial period',
    trialValue: '{n} days per machine',
    disabled: 'Disabled',
    versions: 'Released versions',
    versionsHelp: 'Used to enforce the updates period of perpetual licenses.',
    colVersion: 'Version',
    colPublished: 'Released',
    deleteVersionConfirm: 'Delete version {v}?',
    publishDate: 'Release date',
    addVersion: 'Add version',
    features: 'Features / modules',
    colName: 'Name',
    colDefault: 'Default',
    colInTrial: 'In trial',
    noFeatures: 'No features',
    deleteFeatureConfirm: 'Delete feature {code}?',
    featureCodePlaceholder: 'feature-code',
    featureNamePlaceholder: 'Display name',
    editFeatureTitle: 'Edit feature',
    featureCodeHelp: 'It is what the app checks and what licenses store: it cannot be changed. For another code, create a new feature.',
    addFeature: 'Add feature',
    editions: 'Editions',
    editionsHelp:
      'Commercial tiers of the product (Basic, Pro…). They are templates: creating a license copies their features and limits, and the app sees the edition in the certificate.',
    newEdition: '+ New edition',
    newEditionTitle: 'New edition',
    editEditionTitle: 'Edit edition',
    noEditions: 'No editions: licenses are created with the product\'s default features',
    colLimits: 'Limits',
    colLicenses: 'Licenses',
    editionCodeHelp: 'Goes in the certificate and is what the app checks; it cannot be changed later',
    order: 'Order',
    orderHelp: 'From lowest to highest tier',
    editionChangesNote: 'Changes apply to licenses created from now on; existing licenses do not change.',
    deleteEditionConfirm: 'Delete the {name} edition?',
  },
  customers: {
    title: 'Customers',
    subtitle: 'Organisations and end users',
    new: '+ New customer',
    searchPlaceholder: 'Search by name, email, tax ID or reference…',
    colTaxId: 'Tax ID',
    empty: 'No customers',
    editTitle: 'Edit customer',
    newTitle: 'New customer',
    legalName: 'Name / legal name',
    contactEmail: 'Contact email',
    taxId: 'Tax ID',
    externalRef: 'External reference',
    externalRefHelp: 'ID in CRM / billing',
    back: '← Customers',
    newLicense: '+ New license',
    data: 'Details',
    users: 'End users',
    noUsers: 'No users',
    deleteUserConfirm: 'Delete the user?',
    emailPlaceholder: 'email@customer.com',
    licenses: 'Licenses ({n})',
    noLicenses: 'No licenses',
    colKey: 'Key',
    colExpires: 'Expires',
    colLastSeen: 'Last connection',
  },
  machines: {
    title: 'Machines',
    subtitle: 'Computers activated on every license. Only a hash of the hardware is stored, never plain data.',
    searchPlaceholder: 'Search by computer name, fingerprint, key or customer…',
    active: 'Active',
    inactive: 'Deactivated',
    all: 'All',
    colMachine: 'Computer',
    colLicense: 'License',
    colOs: 'OS / version',
    colLastSeen: 'Last seen',
    deactivatedAgo: 'Deactivated {when}',
    deactivateConfirm: 'Force deactivation of {name}?',
  },
  audit: {
    title: 'Audit log',
    subtitle: 'Record of every administrative action and every client application action',
    allActions: 'All actions',
    anyActor: 'Any actor',
    userPlaceholder: 'User (email)…',
    from: 'From',
    to: 'To',
    colActor: 'Actor',
    colAction: 'Action',
    colEntity: 'Entity',
    colDetails: 'Details',
    empty: 'No entries',
    group: {
      license: 'Licenses',
      activation: 'Activations',
      floating: 'Floating',
      trial: 'Trials',
      product: 'Products',
      edition: 'Editions',
      customer: 'Customers',
      webhook: 'Webhooks',
    },
    actor: { admin: 'Admin', client: 'App', system: 'System' },
    actorFilter: { admin: 'Administrators', client: 'Applications', system: 'System' },
    action: {
      'license.created': 'License created',
      'license.updated': 'License updated',
      'license.revoked': 'License revoked',
      'license.suspended': 'License suspended',
      'license.reactivated': 'License reactivated',
      'license.extended': 'License extended',
      'license.seats_changed': 'Seats changed',
      'license.expired': 'License expired',
      'activation.created': 'Machine activated',
      'activation.deactivated': 'Machine deactivated (customer)',
      'activation.force_deactivated': 'Machine deactivated (admin)',
      'activation.offline': 'Offline activation',
      'floating.checkout': 'Floating session taken',
      'floating.checkin': 'Floating session returned',
      'floating.force_checkin': 'Floating session released (admin)',
      'trial.started': 'Trial started',
      'product.created': 'Product created',
      'product.updated': 'Product updated',
      'product.deleted': 'Product deleted',
      'feature.created': 'Feature created',
      'feature.updated': 'Feature updated',
      'feature.deleted': 'Feature deleted',
      'edition.created': 'Edition created',
      'edition.updated': 'Edition updated',
      'edition.deleted': 'Edition deleted',
      'version.created': 'Version registered',
      'version.deleted': 'Version deleted',
      'customer.created': 'Customer created',
      'customer.updated': 'Customer updated',
      'customer.deleted': 'Customer deleted',
      'customer_user.created': 'User added',
      'customer_user.deleted': 'User deleted',
      'webhook.created': 'Webhook created',
      'webhook.updated': 'Webhook updated',
      'webhook.deleted': 'Webhook deleted',
    },
  },
  offline: {
    title: 'Offline activation',
    subtitle: 'Activate computers without internet access using signed files',
    invalidJson: 'The file does not contain valid JSON. Upload the .req file generated by the application.',
    notRequest: 'The file is not a Voxel offline activation request.',
    dropTitle: 'Drop the request file here',
    dropHint: 'or click to select it (.req)',
    done: 'Activation generated.',
    doneDetail: '{file} has been downloaded for computer {machine} (license {key}, issued {date}). Copy it to the offline computer and import it from the application.',
    unknownMachine: 'unnamed',
    downloadAgain: 'Download again',
    fileSuffix: 'activation',
    howTitle: 'How it works',
    step1: 'On the offline computer, the application generates a request file (.req).',
    step2: 'Take it to a computer with internet access and upload it here.',
    step3: 'A signed response file (.lic) is downloaded: copy it to the computer and import it in the application.',
    howNote: 'Customers can also do this without an account in the self-service portal. The activation takes one seat of the license and is recorded in the audit log. Floating licenses require a connection.',
    portalLink: 'Open the self-service portal',
    portalTitle: 'Offline activation · Self-service portal',
  },
  webhooks: {
    title: 'Webhooks',
    subtitle: 'Outgoing notifications signed with HMAC-SHA256 to integrate the store, CRM or billing',
    new: '+ New webhook',
    secretTitle: 'Signing secret',
    secretNote: '(shown only now; store it in the receiving system):',
    verification: 'Verification:',
    verificationDetail: 'against the header',
    colUrl: 'URL',
    colEvents: 'Events',
    allEvents: 'All',
    enabled: 'Active',
    paused: 'Paused',
    empty: 'No webhooks configured',
    deliveries: 'Deliveries',
    test: 'Test',
    pause: 'Pause',
    resume: 'Resume',
    deleteConfirm: 'Delete the webhook?',
    newTitle: 'New webhook',
    url: 'Destination URL',
    events: 'Events',
    eventsHelp: 'No selection = all events',
    deliveriesTitle: 'Latest deliveries',
    colEvent: 'Event',
    colAttempts: 'Attempts',
    noDeliveries: 'No deliveries',
    delivery: { delivered: 'Delivered', pending: 'Pending', failed: 'Failed' },
  },
  manual: {
    title: 'User manual',
    subtitle: 'Guide to the administration panel and to common support tasks',
    print: 'Print',
    downloadWord: 'Download as Word',
    contents: 'Contents',
  },
  errors: {
    network_error: 'Cannot connect to the API',
    unauthorized: 'Session expired or invalid. Please sign in again.',
    forbidden: 'You are not allowed to perform this action',
    not_found: 'Not found',
    rate_limited: 'Too many requests; wait a few seconds',
    invalid_license: 'Invalid license key',
    product_mismatch: 'The license belongs to another product',
    license_suspended: 'The license is suspended',
    license_revoked: 'The license has been revoked',
    license_expired: 'The license has expired',
    version_not_allowed: 'The license does not allow this application version',
    seat_limit_reached: 'The license has no free seats',
    no_floating_seats: 'All floating sessions are in use',
    not_activated: 'The machine is not activated',
    not_floating: 'Floating licenses require a connection: offline activation is not supported',
    trial_already_used: 'This machine has already used its trial period',
    trial_not_available: 'This product does not offer a trial period',
    invalid_offline_request: 'The file is not a valid offline activation request',
    internal_error: 'Internal server error',
  },
};
